import { t } from '@lingui/core/macro';
import { useMemo, useState } from 'react';

import { useVendorList } from '@/api/vendor';
import {
  ComboboxPickerFilter,
  encodeFilterFieldName,
  FilterField,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import type { FilterComponentProps } from '@/providers/TableState';

type Props = FilterComponentProps & {
  mode?: 'multiple' | 'single';
  hasExclude?: boolean;
  channelId?: string;
};

const VendorFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  hasExclude = true,
  channelId,
}: Props) => {
  const form = useFilterForm();
  const { vendors, isLoading, error } = useVendorList({ channelId });
  const [searchText, setSearchText] = useState<string>('');

  const vendorsWithChannels = useMemo(
    () =>
      vendors?.map((vendor) => ({
        id: vendor.id,
        name: `${vendor.name} - ${vendor.channel?.name}`,
      })),
    [vendors],
  );
  const filteredVendorsWithChannels = useMemo(
    () =>
      vendorsWithChannels?.filter((vendor) =>
        vendor.name.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [vendorsWithChannels, searchText],
  );

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="vendor-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <ComboboxPickerFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              mode={mode}
              singlePlaceholder={t`Select Vendor...`}
              multiplePlaceholder={t`Select Vendors...`}
              multipleLoadingPlaceholder={t`Loading Vendors...`}
              items={vendorsWithChannels}
              isLoading={isLoading}
              error={error}
              notFoundContent={t`No vendors were found...`}
              hasExclude={hasExclude}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={filteredVendorsWithChannels}
              isLoading={isLoading}
              error={error}
              searchText={searchText}
              onSearch={setSearchText}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default VendorFilter;
